/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file LicenseFilterBtns.tsx
 * @desc Created on Thu May 15 2025 20:59:07
 * @copyright All rights reserved @ Inarix
 */

import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Tooltip } from "@mui/material";
import { RemoveRedEye } from "@mui/icons-material";
import {
  selectAvailPackCount,
  selectExpiringCount,
  selectLowOrEmptyCount,
  selectUnassignedCount,
  filterselectors,
  setFilterModel,
} from "../../redux/slices/licenses/licenseList";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { fetchLicenses } from "../../redux/actions/licenses/licenseListAsyncActions";

enum Colors {
  green = "#4CAF50",
  blue = "#3478F6",
  red = "#E57373",
  orange = "#FF9800",
  grey = "rgba(33, 29, 38, .38)",
}

const FilterButton: FC<{
  title: string;
  value: number;
  color: Colors;
  emptyColor?: Colors;
  handler?: () => void;
  filterModel?: any;
  buttonName?: string;
  tooltipText?: string;
}> = ({
  handler,
  title,
  value,
  color,
  emptyColor,
  filterModel,
  buttonName,
  tooltipText,
}) => {
  const searchValue = filterModel?.items?.find(
    (item) => item.value === buttonName,
  )?.value;

  const manageEyeColor = () => {
    if (searchValue) {
      return "license-eye-active";
    } else {
      return "license-eye-inactive";
    }
  };

  return (
    <Box onClick={handler} sx={{ cursor: handler ? "pointer" : "default" }}>
      <Stack direction="row">
        <h6>{title}</h6>
        {handler ? (
          <RemoveRedEye className={`license-eye ${manageEyeColor()}`} />
        ) : null}
      </Stack>
      <Tooltip title={tooltipText} placement="top">
        <h3
          style={{
            color: value === 0 ? emptyColor || color : color,
          }}
        >
          {value}
        </h3>
      </Tooltip>
    </Box>
  );
};
export function LicenseFilterBtns() {
  const [t] = useTranslation("global");
  const unassignedCount = useSelector(selectUnassignedCount);
  const lowOrEmptyCount = useSelector(selectLowOrEmptyCount);
  const expiringCount = useSelector(selectExpiringCount);
  const availPackCount = useSelector(selectAvailPackCount);
  const { filterModel } = useSelector(filterselectors);

  const SetActions = () => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    return {
      setFilterModels: (filterModel) => {
        dispatch(setFilterModel(filterModel));
      },
      getLicenses: () => {
        dispatch(fetchLicenses());
      },
    };
  };

  const { setFilterModels, getLicenses } = SetActions();

  const manageFilterModel = (filterName: string) => {
    const existingFilter = filterModel?.items?.find(
      (item) => item.value === filterName,
    );

    if (existingFilter) {
      const newFilterModel = {
        ...filterModel,
        items: filterModel.items.filter((item) => item.value !== filterName),
      };

      setFilterModels(newFilterModel);
    } else {
      const newFilterModel = {
        ...filterModel,
        items: [
          ...(filterModel?.items || []),
          {
            column: t("licenses.licensesStatus"),
            columnType: undefined,
            field: "licensesStatus",
            filter: "LOCAL",
            queryFormat: "",
            service: "special-licenses",
            operator: "$eq",
            realOperator: "$eq",
            value: filterName,
          },
        ],
      };
      setFilterModels(newFilterModel);
    }
  };

  useEffect(() => {
    getLicenses();
  }, [unassignedCount, lowOrEmptyCount, expiringCount, availPackCount]);

  return (
    <div className="license-filter-buttons">
      <div className="license-filter-buttons-child">
        <FilterButton
          color={Colors.blue}
          emptyColor={Colors.grey}
          title={t("licenses.unassigned")}
          value={unassignedCount}
          handler={() => manageFilterModel("unassigned")}
          filterModel={filterModel}
          buttonName="unassigned"
          tooltipText={t("licenses.unassigned_desc")}
        />
        <FilterButton
          color={Colors.orange}
          emptyColor={Colors.green}
          title={t("licenses.lowOrEmpty")}
          value={lowOrEmptyCount}
          handler={() => manageFilterModel("runningLow")}
          filterModel={filterModel}
          buttonName="runningLow"
          tooltipText={t("licenses.lowOrEmpty_desc")}
        />
        <FilterButton
          color={Colors.red}
          title={t("licenses.expSoon")}
          handler={() => manageFilterModel("expiringSoon")}
          value={expiringCount}
          filterModel={filterModel}
          buttonName="expiringSoon"
          tooltipText={t("licenses.expSoon_desc")}
        />
        <FilterButton
          color={Colors.green}
          emptyColor={Colors.grey}
          title={t("licenses.availPacks")}
          value={availPackCount}
          tooltipText={t("licenses.availablePacks")}
        />
      </div>
    </div>
  );
}
